import React, { useState, useEffect } from "react";
import { Box, Modal, IconButton } from "@mui/material";
import { FaTimes } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import BtnLoader from "../../../../Components/Common/Loader/BtnLoader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 400,
  bgcolor: "background.paper",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
};

const AddBannerForm = ({
  addBannerModal,
  handleClose,
  mode,
  initialData,
  fetchBanners,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    // duration: "",
    category: "",
    description: "",
    image: "",
    validTill: "",
  });

  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        name: initialData.name || "",
        // duration: initialData.duration || "",
        category: initialData.category || "",
        description: initialData.description || "",
        image: initialData.img || "",
        validTill: initialData.validTill
          ? initialData.validTill.includes("T")
            ? initialData.validTill.split("T")[0]
            : initialData.validTill.split(" ")[0] // handle IST format
          : "",
      });
    } else {
      setFormData({
        name: "",
        // duration: "",
        category: "",
        description: "",
        image: "",
        validTill: "",
      });
    }
  }, [mode, initialData, addBannerModal]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "image" ? files[0] : value,
    }));
  };

  const uploadSignedImage = async (file) => {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/cloudinary/`);
    const { timestamp, signature, api_key, cloud_name } = res.data;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("api_key", api_key);
    fd.append("timestamp", timestamp);
    fd.append("signature", signature);

    const uploadRes = await fetch(
      `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
      { method: "POST", body: fd }
    );
    const data = await uploadRes.json();
    return data.secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.validTill) {
      Swal.fire("Error", "Please select a valid future date.", "error");
      return;
    }

    const selectedDate = new Date(formData.validTill);
    const today = new Date();
    if (selectedDate < today.setHours(0, 0, 0, 0)) {
      Swal.fire("Error", "Valid Till date cannot be in the past.", "error");
      return;
    }

    if (!formData.image && !initialData?.img) {
      Swal.fire("Error", "Please upload a banner image.", "error");
      return;
    }

    handleClose();

    Swal.fire({
      title: mode === "edit" ? "Updating Banner..." : "Creating Banner...",
      text: "Please wait...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let imageUrl = initialData?.img || "";
      if (formData.image && typeof formData.image !== "string") {
        imageUrl = await uploadSignedImage(formData.image);
      }
      if (removeImage) imageUrl = "";

      const payload = {
        name: formData.name,
        // duration: formData.duration,
        category: formData.category,
        description: formData.description,
        img: imageUrl,
        validTill: formData.validTill, // ✅ include date
      };

      const endpoint =
        mode === "edit"
          ? `${process.env.REACT_APP_API_URL}/banner/update-banner/${initialData._id}`
          : `${process.env.REACT_APP_API_URL}/banner/create-banner`;

      const method = mode === "edit" ? "put" : "post";

      await axios[method](endpoint, payload);

      Swal.fire({
        icon: "success",
        title: `Banner ${mode === "edit" ? "Updated" : "Created"}!`,
        text: "Your banner has been saved successfully.",
      });

      if (typeof fetchBanners === "function") {
        fetchBanners(formData.category || initialData?.category || "Home");
      }
    } catch (error) {
      Swal.fire("Error", "Failed to save banner. Please try again.", "error");
    } finally {
      setFormData({
        name: "",
        // duration: "",
        category: "",
        description: "",
        image: "",
        validTill: "",
      });
      setRemoveImage(false);
    }
  };

  return (
    <Modal open={addBannerModal} onClose={handleClose}>
      <Box sx={style}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
        >
          <FaTimes />
        </IconButton>

        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="mb-2 text-center text-2xl font-bold">
            {mode === "edit" ? "Edit Banner" : "Add Banner"}
          </h2>

          <input
            type="text"
            name="name"
            placeholder="Banner Name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full rounded-md border border-gray-300 p-2"
          />

          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="w-full rounded-md border border-gray-300 p-2"
          >
            <option value="">Select Category</option>
            <option value="Home">Home</option>
            <option value="Buddies">Buddies</option>
            <option value="Gangs">Gangs</option>
          </select>

          {/* <input
            type="text"
            name="duration"
            placeholder="Duration (e.g., 3 months)"
            value={formData.duration}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              handleChange({ target: { name: "duration", value } });
            }}
            required
            className="w-full rounded-md border border-gray-300 p-2"
          /> */}

          {/* ✅ New Valid Till field */}
          <input
            type={formData.validTill ? "date" : "text"}
            name="validTill"
            value={formData.validTill}
            min={new Date().toISOString().split("T")[0]}
            onFocus={(e) => (e.target.type = "date")}
            onBlur={(e) => {
              if (!formData.validTill) e.target.type = "text";
            }}
            onChange={handleChange}
            required
            placeholder="Select banner expiry date"
            className="w-full rounded-md border border-gray-300 p-2 text-gray-700"
          />

          <textarea
            name="description"
            placeholder="Description (optional)"
            value={formData.description}
            onChange={handleChange}
            className="w-full rounded-md border border-gray-300 p-2"
          />

          <input
            type="file"
            name="image"
            accept="image/*"
            onChange={handleChange}
            required={mode === "add" || removeImage}
            className="w-full rounded-md border border-gray-300 p-2"
          />

          {formData.image && typeof formData.image === "string" && (
            <div className="relative mt-3 inline-block w-28 sm:w-32 md:w-40">
              <img
                src={formData.image}
                alt="Banner Preview"
                className="h-auto max-h-32 w-full rounded-md border border-gray-300 object-cover shadow-sm"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center rounded-md bg-[#c51369] p-2 text-white transition hover:bg-[#a10f5b]"
          >
            {loading ? <BtnLoader /> : mode === "edit" ? "Update" : "Submit"}
          </button>
        </form>
      </Box>
    </Modal>
  );
};

export default AddBannerForm;
